
import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Form, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormMessage,
  FormDescription 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

const currencies = [
  { value: "USD", label: "USD - US Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "AED", label: "AED - UAE Dirham" },
  { value: "SGD", label: "SGD - Singapore Dollar" },
  { value: "AUD", label: "AUD - Australian Dollar" },
  { value: "CAD", label: "CAD - Canadian Dollar" },
];

const CreatePerkProgram = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm({
    defaultValues: {
      name: "",
      code: "",
      location: "",
      city: "",
      country: "",
      websiteUrl: "",
      loyaltyPointName: "",
      defaultCurrency: "USD",
    },
  });

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    console.log("Form submitted with data:", data);
    
    try {
      const { error } = await supabase
        .from('programs')
        .insert({
          name: data.name,
          code: data.code,
          location: data.location,
          city: data.city,
          country: data.country,
          website_url: data.websiteUrl,
          loyalty_point_name: data.loyaltyPointName,
          default_currency: data.defaultCurrency,
          status: 'Active',
          enrolled_members: 0,
          completion_rate: 0
        });

      if (error) {
        console.error('Error creating program:', error);
        toast.error("Failed to create program. Please try again.");
        return;
      }

      toast.success("Program created successfully!");
      navigate("/perkpoint/programs");
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/perkpoint/programs">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Create New Program</h1>
          <p className="text-gray-500">Create a new loyalty program</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          <Card className="border shadow-sm overflow-hidden bg-white">
            <CardHeader className="bg-gray-50 border-b border-gray-100">
              <CardTitle className="text-lg font-medium">Program Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{ required: "Program name is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Program Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Premium Rewards" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="code"
                  rules={{ required: "Program code is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Program Code*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. PREM-001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Business Bay" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Dubai" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. United Arab Emirates" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. https://example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border shadow-sm overflow-hidden bg-white">
            <CardHeader className="bg-gray-50 border-b border-gray-100">
              <CardTitle className="text-lg font-medium">Loyalty Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="loyaltyPointName"
                  rules={{ required: "Loyalty point name is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loyalty Point Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Stars, Points" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="defaultCurrency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Currency*</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {currencies.map((currency) => (
                            <SelectItem key={currency.value} value={currency.value}>
                              {currency.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate("/perkpoint/programs")}>
              Cancel
            </Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={isSubmitting}>
              <Save className="h-4 w-4 mr-2" /> 
              {isSubmitting ? "Creating..." : "Create Program"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CreatePerkProgram;
